import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useLang } from '../../context/LangContext';
import { templates } from '../../content/learning';
import { getLocalized } from '../../content/learning/getLocalized';

const ACCENT = '#00d4aa';

const INPUT_STYLE = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 14px',
  background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
  fontFamily: 'Tajawal, sans-serif', fontSize: 14,
  outline: 'none', transition: 'border-color 0.2s',
};

function OutputBox({ label, text, onCopy, copyLabel }) {
  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid rgba(0,212,170,0.18)',
      borderRadius: 'var(--radius-sm)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 14px', borderBottom: '1px solid var(--border)',
        background: 'rgba(0,212,170,0.05)',
      }}>
        <span style={{ fontSize: 12, color: ACCENT, fontWeight: 700 }}>{label}</span>
        <button onClick={onCopy} style={{
          background: 'none', border: `1px solid rgba(0,212,170,0.4)`,
          borderRadius: 6, padding: '3px 12px', color: ACCENT,
          cursor: 'pointer', fontSize: 12, fontFamily: 'Tajawal, sans-serif',
          transition: 'var(--transition)',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,170,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >
          {copyLabel}
        </button>
      </div>
      <p style={{ margin: 0, padding: '14px 18px', color: 'var(--text)', lineHeight: 1.9, fontSize: 14 }}>
        {text}
      </p>
    </div>
  );
}

export default function GapWorksheet() {
  const { t, lang } = useLang();
  const [typeIdx, setTypeIdx] = useState(0);
  const [topic, setTopic] = useState('');
  const [gap,   setGap]   = useState('');

  const types = templates.gapStatements;
  const selected = types[typeIdx];

  const fill = (tmpl) =>
    tmpl
      .replace(/\{topic\}/g, topic.trim() || '___')
      .replace(/\{gap\}/g,   gap.trim()   || '___');

  const hasInput = topic.trim() || gap.trim();

  const mainText = useMemo(
    () => hasInput ? fill(getLocalized(selected.template, lang)) : '',
    [selected, topic, gap, lang]
  );
  const enText = useMemo(
    () => (hasInput && lang !== 'en') ? fill(selected.template.en) : '',
    [selected, topic, gap, lang]
  );

  const copy = (text) =>
    navigator.clipboard.writeText(text)
      .then(() => toast.success(t.learning?.copied || 'تم النسخ!'))
      .catch(() => {});

  const topicLabel = (t.learning?.topicLabel || 'الموضوع {topic}').replace('{topic}', '');
  const gapLabel   = (t.learning?.gapLabel   || 'الجانب الناقص {gap}').replace('{gap}', '');

  return (
    <div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.75, fontSize: 14 }}>
        {t.learning?.gapInstruction}
      </p>

      {/* Gap type chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
        {types.map((g, i) => (
          <button
            key={i}
            onClick={() => setTypeIdx(i)}
            style={{
              padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
              border: i === typeIdx ? `1.5px solid ${ACCENT}` : '1px solid var(--border)',
              background: i === typeIdx ? 'rgba(0,212,170,0.12)' : 'var(--surface2)',
              color: i === typeIdx ? ACCENT : 'var(--text-dim)',
              fontFamily: 'Tajawal, sans-serif', fontSize: 13,
              transition: 'var(--transition)',
            }}
          >
            {getLocalized(g.label, lang)}
          </button>
        ))}
      </div>

      {/* Inputs */}
      <div style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            {topicLabel} <span style={{ color: ACCENT, fontFamily: 'monospace' }}>{'{topic}'}</span>
          </label>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t.learning?.topicPlaceholder}
            style={INPUT_STYLE}
            onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
            onBlur={(e)  => { e.target.style.borderColor = 'var(--border)'; }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            {gapLabel} <span style={{ color: ACCENT, fontFamily: 'monospace' }}>{'{gap}'}</span>
          </label>
          <input
            value={gap}
            onChange={(e) => setGap(e.target.value)}
            placeholder={t.learning?.gapPlaceholder}
            style={INPUT_STYLE}
            onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
            onBlur={(e)  => { e.target.style.borderColor = 'var(--border)'; }}
          />
        </div>
      </div>

      {/* Live output */}
      {hasInput ? (
        <div style={{ display: 'grid', gap: 14 }}>
          <OutputBox
            label={getLocalized({ ar: 'النص المولّد', en: 'Generated Text', fa: 'متن تولیدشده' }, lang)}
            text={mainText}
            copyLabel={t.learning?.copy}
            onCopy={() => copy(mainText)}
          />
          {enText && (
            <OutputBox
              label={t.learning?.outputEn || 'English'}
              text={enText}
              copyLabel={t.learning?.copy}
              onCopy={() => copy(enText)}
            />
          )}
        </div>
      ) : (
        <div style={{
          textAlign: 'center', padding: '40px 24px',
          color: 'var(--text-muted)', fontSize: 14,
          border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)',
        }}>
          {t.learning?.enterInputs}
        </div>
      )}
    </div>
  );
}
